import React from 'react';
import {
    Button,
    Dropdown,
    Field,
    Input,
    Option,
    Spinner,
    Switch,
    Text,
    Textarea,
    Tooltip,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import { Info16Regular, Info24Regular } from '@fluentui/react-icons';
import { useFieldArray, useForm } from 'react-hook-form';
import AgentFieldLabel from '../shared/AgentFieldLabel';
import { getAcceptedAgentUploadExtensions, validateAndSanitizeAgentUploadFiles } from '../../../utils/FileUtils';
import { AlwaysOnScrollBarStyles, customColors, customFonts } from '../../../../styles';

import { useCreateAgentPage } from '../hooks/useCreateAgentPage';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft24Regular } from '@fluentui/react-icons';
import FunctionToolsFieldArray from '../shared/FunctionToolsFieldArray';
import {
    CreateAgentFormValues,
    toolOptions,
    companyEmailPattern,
    DESCRIPTION_MAX_LENGTH,
    INSTRUCTIONS_MAX_LENGTH,
    CARD_SHORT_DESCRIPTION_MAX_LENGTH,
    DISCLAIMER_MAX_LENGTH,
    KNOWLEDGE_BASE_MAX_LENGTH,
    SHORT_ENTRY_MAX_LENGTH,
    PROMPT_DESCRIPTION_MAX_LENGTH,
    PROMPT_MAX_ITEMS,
    FAQ_MAX_ITEMS,
    CAPABILITY_MAX_LENGTH,
    CAPABILITIES_MAX_ITEMS,
    RELATED_LINK_MAX_ITEMS,
    AGENT_UPLOAD_MAX_FILE_SIZE_MB,
    AGENT_UPLOAD_MAX_FILE_COUNT,
} from '../shared/AgentFormShared';
import { mapFunctionToolsFormToRequest } from '../shared/FunctionToolForm';

const useStyles = makeStyles({
    root: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        ...shorthands.overflow('auto'),
        backgroundColor: customColors.protivitiOffWhite,
        ...AlwaysOnScrollBarStyles,
    },
    content: {
        width: '70%',
        maxWidth: '980px',
        ...shorthands.padding('40px', '20px', '40px', '20px'),
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        boxSizing: 'border-box',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('12px'),
        ...shorthands.padding('16px'),
        ...shorthands.borderRadius('12px'),
        backgroundColor: customColors.white,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
        border: `1px solid ${customColors.lightGray}`,
    },
    title: {
        marginBottom: '20px',
        fontSize: '3em',
        fontWeight: '500',
        display: 'flex',
        justifyContent: 'center',
        color: customColors.summarizeButtonDarker,
        fontFamily: customFonts.TitleFont,
        lineHeight: '80%',
    },
    subtitle: {
        fontSize: '2em',
        marginBottom: '10px',
        marginTop: '-10px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: '300',
        color: customColors.headerLightGray,
        fontFamily: customFonts.TitleFont,
    },
    fieldLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        verticalAlign: 'middle',
    },
    fieldName: {
        marginBottom: '5px',
        fontSize: '1.5em',
        fontWeight: '500',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        color: customColors.summarizeButtonDarker,
        fontFamily: customFonts.TitleFont,
    },
    tooltipIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: customColors.summarizeButtonDarker,
        cursor: 'pointer',
        transform: 'scale(0.8)',
        verticalAlign: 'middle',
        marginTop: '1px',
    },
    tooltipContent: {
        maxWidth: '360px',
        whiteSpace: 'normal',
        fontFamily: customFonts.Barlow,
        fontSize: '13px',
        lineHeight: 1.4,
    },
    characterCount: {
        fontFamily: customFonts.Barlow,
        color: customColors.headerLightGray,
        fontSize: '12px',
        textAlign: 'right',
    },
    uploadImageRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
    },
    iconPreview: {
        width: '48px',
        height: '48px',
        borderRadius: '8px',
        objectFit: 'cover',
    },
    textHint: {
        fontFamily: customFonts.Barlow,
        color: customColors.headerLightGray,
        fontSize: '14px',
    },
    mcpOptionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    mcpOptionInfoIcon: {
        color: tokens.colorNeutralForeground3,
        flexShrink: 0,
    },
    uploadRow: {
        display: 'flex',
        ...shorthands.gap('10px'),
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
    uploadRowRight: {
        display: 'flex',
        ...shorthands.gap('10px'),
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
    },
    fileTable: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: customColors.white,
    },
    fileTableHeader: {
        display: 'grid',
        gridTemplateColumns: '48px 1fr 180px',
        alignItems: 'center',
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.padding('10px', '12px'),
        ...shorthands.borderBottom('1px', 'solid', customColors.lightGray),
        columnGap: '12px',
    },
    fileTableRow: {
        display: 'grid',
        gridTemplateColumns: '48px 1fr 180px',
        alignItems: 'center',
        ...shorthands.padding('10px', '12px'),
        columnGap: '12px',
        ...shorthands.borderBottom('1px', 'solid', customColors.lightGray),
        ':last-child': {
            borderBottom: 'none',
        },
    },
    fileTableHeaderText: {
        fontFamily: customFonts.TitleFont,
        color: customColors.summarizeButtonDarker,
        fontSize: '16px',
        fontWeight: '500',
    },
    fileNameText: {
        fontSize: '14px',
        fontWeight: '400',
    },
    fileDateText: {
        fontSize: '14px',
        fontWeight: '400',
    },
    inlineGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: 'fit-content',
    },
    buttonRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        ...shorthands.gap('10px'),
        ...shorthands.margin('8px', '0', '0', '0'),
        ...shorthands.padding('0', '0', '40px', '0'),
    },
    errorText: {
        color: '#b42318',
        fontFamily: customFonts.Barlow,
        fontSize: '13px',
    },
    select: {
        width: '100%',
        minHeight: '36px',
        ...shorthands.border('2px', 'solid', customColors.headerLightGray),
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('6px', '8px'),
        backgroundColor: customColors.white,
        fontFamily: customFonts.Barlow,
    },
    removeButton: {
        alignSelf: 'flex-start',
    },
    compactButton: {
        width: 'fit-content',
        ...shorthands.borderRadius('5px'),
    },
    subItemCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        ...shorthands.padding('14px'),
        ...shorthands.borderRadius('10px'),
        backgroundColor: '#fafafa',
    },
    inlineActionRow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '10px',
    },
    addButtonRow: {
        ...shorthands.padding('0', '0', '0', '14px'),
    },
    hiddenFileInput: {
        display: 'none',
    },
    actionButton: {
        ...shorthands.borderRadius('5px'),
    },
    collapsibleHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        ...shorthands.padding(0),
        ...shorthands.border('0'),
        textAlign: 'left',
    },
    collapsibleHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    collapseIcon: {
        color: customColors.summarizeButtonDarker,
    },
    metadataSeparator: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        ...shorthands.margin('32px', '0'),
    },
    metadataSeparatorText: {
        color: customColors.summarizeButtonDarker,
        fontFamily: customFonts.TitleFont,
        fontSize: '2em',
        fontWeight: '500',
        lineHeight: 1,
    },
    metadataSeparatorLine: {
        flexGrow: 1,
        height: '1px',
        backgroundColor: customColors.lightGray,
    },
    fullWidth: {
        width: '100%',
    },
    backButtonRow: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-start',
        ...shorthands.margin('0px', '0px', '8px', '0px'),
    },
    backButton: {
        height: '28px',
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        fontWeight: '700',
        textDecoration: 'underline',
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '10px'),
        '&:hover': {
            color: customColors.summarizeButtonDarker,
        },
    },
});

// TODO: wire up image uploads and agent owner fields
const CreateAgentPage: React.FC = () => {
    const styles = useStyles();
    const vm = useCreateAgentPage();
    const navigate = useNavigate();

    const fileSearchInputRef = React.useRef<HTMLInputElement | null>(null);
    const codeInterpreterInputRef = React.useRef<HTMLInputElement | null>(null);
    const imageInputRef = React.useRef<HTMLInputElement | null>(null);
    const [uploadedFileSearchFiles, setUploadedFileSearchFiles] = React.useState<File[]>([]);
    const [uploadedCodeInterpreterFiles, setUploadedCodeInterpreterFiles] = React.useState<File[]>([]);
    const [selectedFileSearchFileNames, setSelectedFileSearchFileNames] = React.useState<Set<string>>(new Set());
    const [selectedCodeInterpreterFileNames, setSelectedCodeInterpreterFileNames] = React.useState<Set<string>>(
        new Set(),
    );
    const [fileSearchErrors, setFileSearchErrors] = React.useState<string[]>([]);
    const [codeInterpreterErrors, setCodeInterpreterErrors] = React.useState<string[]>([]);
    const [selectedImageFile, setSelectedImageFile] = React.useState<File | null>(null);

    const models = vm.availableModels;
    const maxFileCount = AGENT_UPLOAD_MAX_FILE_COUNT;
    const maxFileSizeMb = AGENT_UPLOAD_MAX_FILE_SIZE_MB;
    const availableMcpServers = vm.availableMcpServers;
    const isMcpServersLoading = vm.isMcpServersLoading;

    const {
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<CreateAgentFormValues>({
        defaultValues: {
            name: '',
            description: '',
            model: models[0] ?? 'gpt-5.4-mini',
            instructions: '',
            tools: [],
            functionTools: [],
            allowFileSearchAtConversationLevel: false,
            allowCodeInterpreterAtConversationLevel: false,
            selectedMcpServerIds: [],
            cardShortDescription: '',
            disclaimer: '',
            knowledgeBaseContent: '',
            promptLibrary: [{ title: '', prompt: '' }],
            faqs: [{ question: '', answer: '' }],
            ownerInfo: [
                {
                    userId: '',
                    givenName: '',
                    surName: '',
                    userPrincipalName: '',
                    mail: '',
                    photoUrl: '',
                },
            ],
            supportAddress: '',
            capabilities: [{ value: '' }],
            relatedLinks: [{ title: '', link: '' }],
        },
    });

    const {
        fields: promptFields,
        append: appendPrompt,
        remove: removePrompt,
    } = useFieldArray({ control, name: 'promptLibrary' });
    const { fields: faqFields, append: appendFaq, remove: removeFaq } = useFieldArray({ control, name: 'faqs' });
    const {
        fields: ownerFields,
        append: appendOwner,
        remove: removeOwner,
    } = useFieldArray({ control, name: 'ownerInfo' });
    const {
        fields: capabilityFields,
        append: appendCapability,
        remove: removeCapability,
    } = useFieldArray({ control, name: 'capabilities' });
    const {
        fields: relatedLinkFields,
        append: appendRelatedLink,
        remove: removeRelatedLink,
    } = useFieldArray({ control, name: 'relatedLinks' });

    const selectedTools = watch('tools');
    const enableFileSearch = selectedTools.includes('fileSearch');
    const enableCodeInterpreter = selectedTools.includes('codeInterpreter');
    const enableFunctionCalling = selectedTools.includes('functionCalling');
    const enableMcpServer = selectedTools.includes('mcpServer');
    const fileSearchAcceptedExtensions = React.useMemo(() => getAcceptedAgentUploadExtensions(false, true), []);
    const codeInterpreterAcceptedExtensions = React.useMemo(() => getAcceptedAgentUploadExtensions(true, false), []);

    const selectedToolLabels = React.useMemo(
        () =>
            toolOptions
                .filter((tool) => selectedTools.includes(tool.value))
                .map((tool) => tool.label)
                .join(', '),
        [selectedTools],
    );

    const validateCompanyEmail = (value?: string): true | string => {
        if (!value?.trim()) {
            return true;
        }

        return companyEmailPattern.test(value.trim()) ? true : 'Must be a roberthalf.com or protiviti.com address.';
    };

    const handleToolChange = (values: string[]): void => {
        setValue('tools', values, { shouldValidate: true, shouldDirty: true });
        setFileSearchErrors([]);
        setCodeInterpreterErrors([]);
    };

    const handleOpenFilePicker = (inputRef: React.RefObject<HTMLInputElement>): void => {
        inputRef.current?.click();
    };

    const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setSelectedImageFile(file);
        event.target.value = '';
    };

    const handleUploadFiles = async (
        event: React.ChangeEvent<HTMLInputElement>,
        options: {
            currentFiles: File[];
            setCurrentFiles: React.Dispatch<React.SetStateAction<File[]>>;
            acceptedExtensions: string[];
            setErrors: React.Dispatch<React.SetStateAction<string[]>>;
        },
    ): Promise<void> => {
        const inputFiles = event.target.files;
        if (!inputFiles || inputFiles.length === 0) {
            return;
        }

        const totalSelectedCount = uploadedFileSearchFiles.length + uploadedCodeInterpreterFiles.length;

        const result = await validateAndSanitizeAgentUploadFiles(Array.from(inputFiles), {
            currentCount: totalSelectedCount,
            maxFileCount,
            maxFileSizeMb,
            acceptedExtensions: options.acceptedExtensions,
        });

        const existing = new Set(options.currentFiles.map((file) => `${file.name}-${file.size}`));
        const deduped = result.acceptedFiles.filter((file) => !existing.has(`${file.name}-${file.size}`));
        options.setCurrentFiles((previous) => [...previous, ...deduped]);
        options.setErrors(result.errors);
        event.target.value = '';
    };

    const toggleFileSelection = (
        fileName: string,
        setSelectedFiles: React.Dispatch<React.SetStateAction<Set<string>>>,
    ): void => {
        setSelectedFiles((previous) => {
            const next = new Set(previous);
            if (next.has(fileName)) {
                next.delete(fileName);
            } else {
                next.add(fileName);
            }
            return next;
        });
    };

    const handleDeleteSelectedFiles = (
        selectedFileNames: Set<string>,
        setFiles: React.Dispatch<React.SetStateAction<File[]>>,
        setSelectedFiles: React.Dispatch<React.SetStateAction<Set<string>>>,
    ): void => {
        if (selectedFileNames.size === 0) {
            return;
        }

        setFiles((previous) => previous.filter((file) => !selectedFileNames.has(`${file.name}-${file.size}`)));
        setSelectedFiles(new Set());
    };

    const handleToggleSelectAllFiles = (
        uploadedFiles: File[],
        selectedFileNames: Set<string>,
        setSelectedFiles: React.Dispatch<React.SetStateAction<Set<string>>>,
    ): void => {
        if (uploadedFiles.length === 0) {
            return;
        }

        if (selectedFileNames.size === uploadedFiles.length) {
            setSelectedFiles(new Set());
            return;
        }

        setSelectedFiles(new Set(uploadedFiles.map((file) => `${file.name}-${file.size}`)));
    };

    const descriptionValue = watch('description') ?? '';
    const instructionsValue = watch('instructions') ?? '';
    const cardShortDescriptionValue = watch('cardShortDescription') ?? '';
    const disclaimerValue = watch('disclaimer') ?? '';
    const knowledgeBaseValue = watch('knowledgeBaseContent') ?? '';

    const onSubmit = async (values: CreateAgentFormValues): Promise<void> => {
        const metadata: Record<string, unknown> = {};

        if (values.cardShortDescription.trim()) {
            metadata.cardShortDescription = values.cardShortDescription.trim();
        }

        if (values.disclaimer.trim()) {
            metadata.disclaimer = values.disclaimer.trim();
        }

        if (values.knowledgeBaseContent.trim()) {
            metadata.agentKnowledgeBase = { content: values.knowledgeBaseContent.trim() };
        }

        // Prompts are stored under a "Conversation Starters" category by default to fit the format of the ConversationStarterLibrary. Allows for expansion in the future if we want to support multiple categories of prompts.
        const validPrompts = values.promptLibrary
            .map((prompt) => ({
                title: prompt.title?.trim() || undefined,
                prompt: prompt.prompt.trim(),
            }))
            .filter((prompt) => (prompt.title?.length ?? 0) > 0 || prompt.prompt.length > 0);
        if (validPrompts.length > 0) {
            metadata.promptLibrary = [
                {
                    category: 'Conversation Starters',
                    items: validPrompts,
                },
            ];
        }

        const validFaqs = values.faqs.filter((f) => f.question.trim() || f.answer.trim());
        if (validFaqs.length > 0) {
            metadata.faqs = validFaqs;
        }

        const validOwners = values.ownerInfo
            .map((owner) => owner.userPrincipalName?.trim())
            .filter((userPrincipalName): userPrincipalName is string => Boolean(userPrincipalName));
        if (validOwners.length > 0) {
            metadata.agentOwnerInfo = validOwners.map((userPrincipalName) => ({
                userId: '',
                givenName: '',
                surName: '',
                userPrincipalName,
                mail: '',
                photoUrl: '',
            }));
        }

        if (values.supportAddress?.trim()) {
            metadata.supportAddress = values.supportAddress.trim();
        }

        const validCapabilities = values.capabilities.map((c) => c.value.trim()).filter(Boolean);
        if (validCapabilities.length > 0) {
            metadata.capabilities = validCapabilities;
        }

        const validLinks = values.relatedLinks.filter((l) => l.title.trim() || l.link.trim());
        if (validLinks.length > 0) {
            metadata.relatedLinks = validLinks;
        }

        metadata.allowFileSearchAtConversationLevel = values.allowFileSearchAtConversationLevel;
        metadata.allowCodeInterpreterAtConversationLevel = values.allowCodeInterpreterAtConversationLevel;

        const functionTools = enableFunctionCalling ? mapFunctionToolsFormToRequest(values.functionTools) : undefined;

        await vm.onSubmit({
            name: values.name.trim(),
            instructions: values.instructions.trim(),
            description: values.description.trim() || undefined,
            agentIconFile: selectedImageFile ?? undefined,
            model: values.model.trim() || undefined,
            filesForFileSearch: enableFileSearch ? uploadedFileSearchFiles : undefined,
            filesForCodeInterpreter: enableCodeInterpreter ? uploadedCodeInterpreterFiles : undefined,
            enableCodeInterpreter,
            enableFileSearch,
            functionTools,
            mcpServerIds: values.selectedMcpServerIds.length > 0 ? values.selectedMcpServerIds : undefined,
            metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        });
    };

    return (
        <div className={styles.root}>
            <form className={styles.content} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.backButtonRow}>
                    <Button
                        type="button"
                        appearance="transparent"
                        className={styles.backButton}
                        icon={<ArrowLeft24Regular />}
                        onClick={() => navigate('/manage-agents')}
                    >
                        Back to Manage Agents
                    </Button>
                </div>
                <Text className={styles.title}>Create Agent</Text>

                <Field
                    label={
                        <AgentFieldLabel
                            label="Name"
                            tooltip="Provide a unique name for this agent."
                            fieldLabelClassName={styles.fieldLabel}
                            fieldNameClassName={styles.fieldName}
                            tooltipIconClassName={styles.tooltipIcon}
                            tooltipContentClassName={styles.tooltipContent}
                        />
                    }
                    required
                    validationMessage={errors.name?.message}
                    validationState={errors.name ? 'error' : 'none'}
                >
                    <Input
                        {...register('name', {
                            required: 'Name is required.',
                            maxLength: {
                                value: 36,
                                message: 'Name cannot exceed 36 characters.',
                            },
                        })}
                        maxLength={36}
                        placeholder="Agent name"
                    />
                </Field>

                <Field
                    label={
                        <AgentFieldLabel
                            label="Description"
                            tooltip="Provide a brief description of what this agent does or its purpose."
                            fieldLabelClassName={styles.fieldLabel}
                            fieldNameClassName={styles.fieldName}
                            tooltipIconClassName={styles.tooltipIcon}
                            tooltipContentClassName={styles.tooltipContent}
                        />
                    }
                    validationMessage={errors.description?.message}
                    validationState={errors.description ? 'error' : 'none'}
                >
                    <Input
                        {...register('description', {
                            maxLength: {
                                value: DESCRIPTION_MAX_LENGTH,
                                message: `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters.`,
                            },
                        })}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        placeholder="What this agent does"
                    />
                    <Text className={styles.characterCount}>
                        {descriptionValue.length}/{DESCRIPTION_MAX_LENGTH} characters
                    </Text>
                </Field>

                <Field
                    label={
                        <AgentFieldLabel
                            label="Model"
                            tooltip="Select the model that powers your agent. The model determines the capabilities and performance of the agent."
                            fieldLabelClassName={styles.fieldLabel}
                            fieldNameClassName={styles.fieldName}
                            tooltipIconClassName={styles.tooltipIcon}
                            tooltipContentClassName={styles.tooltipContent}
                        />
                    }
                    required
                >
                    <select className={styles.select} {...register('model', { required: 'Model is required.' })}>
                        {models.length > 0 ? (
                            models.map((model) => (
                                <option key={model} value={model}>
                                    {model}
                                </option>
                            ))
                        ) : (
                            <option value="gpt-5.4-mini">gpt-5.4-mini</option>
                        )}
                    </select>
                </Field>

                <Field
                    label={
                        <AgentFieldLabel
                            label="Instructions"
                            tooltip="Specify the persona details that define how this agent behaves or interacts. Personas influence the tone and style of responses."
                            fieldLabelClassName={styles.fieldLabel}
                            fieldNameClassName={styles.fieldName}
                            tooltipIconClassName={styles.tooltipIcon}
                            tooltipContentClassName={styles.tooltipContent}
                        />
                    }
                    required
                    validationMessage={errors.instructions?.message}
                    validationState={errors.instructions ? 'error' : 'none'}
                >
                    <Textarea
                        {...register('instructions', {
                            required: 'Instructions are required.',
                            maxLength: {
                                value: INSTRUCTIONS_MAX_LENGTH,
                                message: `Instructions cannot exceed ${INSTRUCTIONS_MAX_LENGTH} characters.`,
                            },
                        })}
                        maxLength={INSTRUCTIONS_MAX_LENGTH}
                        placeholder="Agent persona"
                        style={{ minHeight: '220px' }}
                    />
                    <Text className={styles.characterCount}>
                        {instructionsValue.length}/{INSTRUCTIONS_MAX_LENGTH} characters
                    </Text>
                </Field>

                <Field
                    label={
                        <AgentFieldLabel
                            label="Agent Tools"
                            tooltip="Select the tools this agent can use, such as File Search or Code Interpreter."
                            fieldLabelClassName={styles.fieldLabel}
                            fieldNameClassName={styles.fieldName}
                            tooltipIconClassName={styles.tooltipIcon}
                            tooltipContentClassName={styles.tooltipContent}
                        />
                    }
                >
                    <Dropdown
                        multiselect={true}
                        selectedOptions={selectedTools}
                        value={selectedToolLabels}
                        placeholder="Select agent tools"
                        onOptionSelect={(_event, data) => handleToolChange(data.selectedOptions)}
                    >
                        {toolOptions.map((tool) => (
                            <Option key={tool.value} value={tool.value}>
                                {tool.label}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>

                {enableFunctionCalling && (
                    <Field
                        label={
                            <AgentFieldLabel
                                label="Function Tools"
                                tooltip="Define custom functions this agent can call, including parameter shapes and strict validation."
                                fieldLabelClassName={styles.fieldLabel}
                                fieldNameClassName={styles.fieldName}
                                tooltipIconClassName={styles.tooltipIcon}
                                tooltipContentClassName={styles.tooltipContent}
                            />
                        }
                    >
                        <FunctionToolsFieldArray
                            control={control}
                            name="functionTools"
                            watch={watch}
                            setValue={setValue}
                        />
                    </Field>
                )}

                {enableMcpServer && (
                    <Field
                        label={
                            <AgentFieldLabel
                                label="MCP Servers"
                                tooltip="Select MCP servers to attach to this agent. MCP servers extend the agent with external tools."
                                fieldLabelClassName={styles.fieldLabel}
                                fieldNameClassName={styles.fieldName}
                                tooltipIconClassName={styles.tooltipIcon}
                                tooltipContentClassName={styles.tooltipContent}
                            />
                        }
                    >
                        {isMcpServersLoading ? (
                            <Spinner size="tiny" label="Loading MCP servers..." labelPosition="after" />
                        ) : (
                            <Dropdown
                                multiselect={true}
                                selectedOptions={watch('selectedMcpServerIds') ?? []}
                                value={
                                    availableMcpServers
                                        .filter((s) => (watch('selectedMcpServerIds') ?? []).includes(s.id))
                                        .map((s) => s.name)
                                        .join(', ') || ''
                                }
                                placeholder={
                                    availableMcpServers.length === 0 ? 'No MCP servers available' : 'Select MCP servers'
                                }
                                disabled={availableMcpServers.length === 0}
                                onOptionSelect={(_event, data) =>
                                    setValue('selectedMcpServerIds', data.selectedOptions, { shouldDirty: true })
                                }
                            >
                                {availableMcpServers.map((server) => (
                                    <Option key={server.id} value={server.id} text={server.name}>
                                        <span className={styles.mcpOptionRow}>
                                            <span>{server.name}</span>
                                            {server.description && (
                                                <Tooltip
                                                    content={server.description}
                                                    relationship="description"
                                                    positioning="after"
                                                    withArrow
                                                >
                                                    <Info16Regular className={styles.mcpOptionInfoIcon} />
                                                </Tooltip>
                                            )}
                                        </span>
                                    </Option>
                                ))}
                            </Dropdown>
                        )}
                    </Field>
                )}

                <Field
                    label={
                        <AgentFieldLabel
                            label="Allow File Search in Conversations"
                            tooltip="When enabled, users can upload files for File Search within individual conversations with this agent."
                            fieldLabelClassName={styles.fieldLabel}
                            fieldNameClassName={styles.fieldName}
                            tooltipIconClassName={styles.tooltipIcon}
                            tooltipContentClassName={styles.tooltipContent}
                        />
                    }
                >
                    <Switch
                        checked={watch('allowFileSearchAtConversationLevel')}
                        onChange={(_event, data) =>
                            setValue('allowFileSearchAtConversationLevel', data.checked, { shouldDirty: true })
                        }
                        label={watch('allowFileSearchAtConversationLevel') ? 'Enabled' : 'Disabled'}
                    />
                </Field>

                <Field
                    label={
                        <AgentFieldLabel
                            label="Allow Code Interpreter in Conversations"
                            tooltip="When enabled, users can upload files for Code Interpreter within individual conversations with this agent."
                            fieldLabelClassName={styles.fieldLabel}
                            fieldNameClassName={styles.fieldName}
                            tooltipIconClassName={styles.tooltipIcon}
                            tooltipContentClassName={styles.tooltipContent}
                        />
                    }
                >
                    <Switch
                        checked={watch('allowCodeInterpreterAtConversationLevel')}
                        onChange={(_event, data) =>
                            setValue('allowCodeInterpreterAtConversationLevel', data.checked, { shouldDirty: true })
                        }
                        label={watch('allowCodeInterpreterAtConversationLevel') ? 'Enabled' : 'Disabled'}
                    />
                </Field>

                {enableFileSearch && (
                    <div className={styles.section}>
                        <div className={styles.fieldLabel}>
                            <Text className={styles.fieldName}>
                                File Search Files ({uploadedFileSearchFiles.length})
                            </Text>
                            <Tooltip
                                relationship="label"
                                positioning="after"
                                content={
                                    <div className={styles.tooltipContent}>Upload files for the File Search tool.</div>
                                }
                            >
                                <Info24Regular className={styles.tooltipIcon} />
                            </Tooltip>
                        </div>
                        <Text className={styles.textHint}>
                            Max files: {maxFileCount}. Max size per file: {maxFileSizeMb}MB.
                        </Text>
                        <Text className={styles.textHint}>Accepted: {fileSearchAcceptedExtensions.join(', ')}</Text>

                        <input
                            ref={fileSearchInputRef}
                            className={styles.hiddenFileInput}
                            type="file"
                            multiple
                            accept={fileSearchAcceptedExtensions.join(',')}
                            onChange={(event) => {
                                void handleUploadFiles(event, {
                                    currentFiles: uploadedFileSearchFiles,
                                    setCurrentFiles: setUploadedFileSearchFiles,
                                    acceptedExtensions: fileSearchAcceptedExtensions,
                                    setErrors: setFileSearchErrors,
                                });
                            }}
                        />

                        <div className={styles.uploadRow}>
                            <Button
                                className={styles.actionButton}
                                type="button"
                                onClick={() => handleOpenFilePicker(fileSearchInputRef)}
                            >
                                Upload Files
                            </Button>
                            <Button
                                className={styles.actionButton}
                                type="button"
                                onClick={() =>
                                    handleDeleteSelectedFiles(
                                        selectedFileSearchFileNames,
                                        setUploadedFileSearchFiles,
                                        setSelectedFileSearchFileNames,
                                    )
                                }
                                disabled={selectedFileSearchFileNames.size === 0}
                            >
                                Delete Selected Files
                            </Button>
                        </div>

                        {fileSearchErrors.length > 0 && (
                            <div>
                                {fileSearchErrors.map((error, index) => (
                                    <Text key={`${error}-${index}`} className={styles.errorText}>
                                        {error}
                                    </Text>
                                ))}
                            </div>
                        )}

                        <div className={styles.fileTable}>
                            <div className={styles.fileTableHeader}>
                                <input
                                    type="checkbox"
                                    checked={
                                        uploadedFileSearchFiles.length > 0 &&
                                        selectedFileSearchFileNames.size === uploadedFileSearchFiles.length
                                    }
                                    onChange={() =>
                                        handleToggleSelectAllFiles(
                                            uploadedFileSearchFiles,
                                            selectedFileSearchFileNames,
                                            setSelectedFileSearchFileNames,
                                        )
                                    }
                                />
                                <Text className={styles.fileTableHeaderText}>File</Text>
                                <Text className={styles.fileTableHeaderText}>Uploaded Date</Text>
                            </div>
                            {uploadedFileSearchFiles.map((file) => (
                                <div key={`${file.name}-${file.size}`} className={styles.fileTableRow}>
                                    <input
                                        type="checkbox"
                                        checked={selectedFileSearchFileNames.has(`${file.name}-${file.size}`)}
                                        onChange={() =>
                                            toggleFileSelection(
                                                `${file.name}-${file.size}`,
                                                setSelectedFileSearchFileNames,
                                            )
                                        }
                                    />
                                    <Text className={styles.fileNameText}>{file.name}</Text>
                                    <Text className={styles.fileDateText}>Pending upload</Text>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {enableCodeInterpreter && (
                    <div className={styles.section}>
                        <div className={styles.fieldLabel}>
                            <Text className={styles.fieldName}>
                                Code Interpreter Files ({uploadedCodeInterpreterFiles.length})
                            </Text>
                            <Tooltip
                                relationship="label"
                                positioning="after"
                                content={
                                    <div className={styles.tooltipContent}>
                                        Upload files for the Code Interpreter tool.
                                    </div>
                                }
                            >
                                <Info24Regular className={styles.tooltipIcon} />
                            </Tooltip>
                        </div>
                        <Text className={styles.textHint}>
                            Max files: {maxFileCount}. Max size per file: {maxFileSizeMb}MB.
                        </Text>
                        <Text className={styles.textHint}>
                            Accepted: {codeInterpreterAcceptedExtensions.join(', ')}
                        </Text>

                        <input
                            ref={codeInterpreterInputRef}
                            className={styles.hiddenFileInput}
                            type="file"
                            multiple
                            accept={codeInterpreterAcceptedExtensions.join(',')}
                            onChange={(event) => {
                                void handleUploadFiles(event, {
                                    currentFiles: uploadedCodeInterpreterFiles,
                                    setCurrentFiles: setUploadedCodeInterpreterFiles,
                                    acceptedExtensions: codeInterpreterAcceptedExtensions,
                                    setErrors: setCodeInterpreterErrors,
                                });
                            }}
                        />

                        <div className={styles.uploadRow}>
                            <Button
                                className={styles.actionButton}
                                type="button"
                                onClick={() => handleOpenFilePicker(codeInterpreterInputRef)}
                            >
                                Upload Files
                            </Button>
                            <Button
                                className={styles.actionButton}
                                type="button"
                                onClick={() =>
                                    handleDeleteSelectedFiles(
                                        selectedCodeInterpreterFileNames,
                                        setUploadedCodeInterpreterFiles,
                                        setSelectedCodeInterpreterFileNames,
                                    )
                                }
                                disabled={selectedCodeInterpreterFileNames.size === 0}
                            >
                                Delete Selected Files
                            </Button>
                        </div>

                        {codeInterpreterErrors.length > 0 && (
                            <div>
                                {codeInterpreterErrors.map((error, index) => (
                                    <Text key={`${error}-${index}`} className={styles.errorText}>
                                        {error}
                                    </Text>
                                ))}
                            </div>
                        )}

                        <div className={styles.fileTable}>
                            <div className={styles.fileTableHeader}>
                                <input
                                    type="checkbox"
                                    checked={
                                        uploadedCodeInterpreterFiles.length > 0 &&
                                        selectedCodeInterpreterFileNames.size === uploadedCodeInterpreterFiles.length
                                    }
                                    onChange={() =>
                                        handleToggleSelectAllFiles(
                                            uploadedCodeInterpreterFiles,
                                            selectedCodeInterpreterFileNames,
                                            setSelectedCodeInterpreterFileNames,
                                        )
                                    }
                                />
                                <Text className={styles.fileTableHeaderText}>File</Text>
                                <Text className={styles.fileTableHeaderText}>Uploaded Date</Text>
                            </div>
                            {uploadedCodeInterpreterFiles.map((file) => (
                                <div key={`${file.name}-${file.size}`} className={styles.fileTableRow}>
                                    <input
                                        type="checkbox"
                                        checked={selectedCodeInterpreterFileNames.has(`${file.name}-${file.size}`)}
                                        onChange={() =>
                                            toggleFileSelection(
                                                `${file.name}-${file.size}`,
                                                setSelectedCodeInterpreterFileNames,
                                            )
                                        }
                                    />
                                    <Text className={styles.fileNameText}>{file.name}</Text>
                                    <Text className={styles.fileDateText}>Pending upload</Text>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className={styles.metadataSeparator}>
                    <div className={styles.metadataSeparatorLine} />
                    <Text className={styles.metadataSeparatorText}>Agent Metadata</Text>
                    <div className={styles.metadataSeparatorLine} />
                </div>

                <Field
                    label={
                        <AgentFieldLabel
                            label="Upload Card Image"
                            tooltip="Upload the image you want to display as a thumbnail for this agent card."
                            fieldLabelClassName={styles.fieldLabel}
                            fieldNameClassName={styles.fieldName}
                            tooltipIconClassName={styles.tooltipIcon}
                            tooltipContentClassName={styles.tooltipContent}
                        />
                    }
                >
                    <input
                        ref={imageInputRef}
                        className={styles.hiddenFileInput}
                        type="file"
                        accept="image/*"
                        onChange={handleImageFileChange}
                    />
                    <div className={styles.uploadImageRow}>
                        <Button
                            className={styles.actionButton}
                            type="button"
                            onClick={() => imageInputRef.current?.click()}
                        >
                            Upload Image
                        </Button>
                        {selectedImageFile && (
                            <img
                                src={URL.createObjectURL(selectedImageFile)}
                                alt="Agent icon preview"
                                className={styles.iconPreview}
                            />
                        )}
                        <Text className={styles.textHint}>{selectedImageFile?.name ?? 'No image selected'}</Text>
                    </div>
                </Field>

                <Field
                    label={
                        <div className={styles.fieldLabel}>
                            <Text className={styles.fieldName}>Card Short Description</Text>
                            <Tooltip
                                relationship="label"
                                positioning="after"
                                content={
                                    <div className={styles.tooltipContent}>
                                        Provide a short summary to display on the agent card.
                                    </div>
                                }
                            >
                                <Info24Regular className={styles.tooltipIcon} />
                            </Tooltip>
                        </div>
                    }
                >
                    <Input
                        {...register('cardShortDescription', {
                            maxLength: {
                                value: CARD_SHORT_DESCRIPTION_MAX_LENGTH,
                                message: `Card short description cannot exceed ${CARD_SHORT_DESCRIPTION_MAX_LENGTH} characters.`,
                            },
                        })}
                        maxLength={CARD_SHORT_DESCRIPTION_MAX_LENGTH}
                        placeholder="Short card description"
                    />
                    <Text className={styles.characterCount}>
                        {cardShortDescriptionValue.length}/{CARD_SHORT_DESCRIPTION_MAX_LENGTH} characters
                    </Text>
                </Field>

                <Field
                    label={
                        <div className={styles.fieldLabel}>
                            <Text className={styles.fieldName}>Disclaimer</Text>
                            <Tooltip
                                relationship="label"
                                positioning="after"
                                content={
                                    <div className={styles.tooltipContent}>
                                        Optional disclaimer text shown alongside the agent details.
                                    </div>
                                }
                            >
                                <Info24Regular className={styles.tooltipIcon} />
                            </Tooltip>
                        </div>
                    }
                >
                    <Textarea
                        {...register('disclaimer', {
                            maxLength: {
                                value: DISCLAIMER_MAX_LENGTH,
                                message: `Disclaimer cannot exceed ${DISCLAIMER_MAX_LENGTH} characters.`,
                            },
                        })}
                        maxLength={DISCLAIMER_MAX_LENGTH}
                        placeholder="Optional disclaimer"
                    />
                    <Text className={styles.characterCount}>
                        {disclaimerValue.length}/{DISCLAIMER_MAX_LENGTH} characters
                    </Text>
                </Field>

                <Field
                    label={
                        <div className={styles.fieldLabel}>
                            <Text className={styles.fieldName}>Agent Knowledge Base</Text>
                            <Tooltip
                                relationship="label"
                                positioning="after"
                                content={
                                    <div className={styles.tooltipContent}>
                                        Provide a knowledge base summary for this agent.
                                    </div>
                                }
                            >
                                <Info24Regular className={styles.tooltipIcon} />
                            </Tooltip>
                        </div>
                    }
                >
                    <Textarea
                        {...register('knowledgeBaseContent', {
                            maxLength: {
                                value: KNOWLEDGE_BASE_MAX_LENGTH,
                                message: `Knowledge base content cannot exceed ${KNOWLEDGE_BASE_MAX_LENGTH} characters.`,
                            },
                        })}
                        maxLength={KNOWLEDGE_BASE_MAX_LENGTH}
                        placeholder="Knowledge base summary"
                    />
                    <Text className={styles.characterCount}>
                        {knowledgeBaseValue.length}/{KNOWLEDGE_BASE_MAX_LENGTH} characters
                    </Text>
                </Field>

                <div className={styles.section}>
                    <div className={styles.fieldLabel}>
                        <Text className={styles.fieldName}>Prompt Library</Text>
                        <Tooltip
                            relationship="label"
                            positioning="after"
                            content={
                                <div className={styles.tooltipContent}>Add reusable prompt entries for this agent.</div>
                            }
                        >
                            <Info24Regular className={styles.tooltipIcon} />
                        </Tooltip>
                    </div>
                    {promptFields.map((field, index) => (
                        <div key={field.id} className={styles.subItemCard}>
                            <Input
                                {...register(`promptLibrary.${index}.title` as const, {
                                    maxLength: {
                                        value: SHORT_ENTRY_MAX_LENGTH,
                                        message: `Prompt title cannot exceed ${SHORT_ENTRY_MAX_LENGTH} characters.`,
                                    },
                                })}
                                maxLength={SHORT_ENTRY_MAX_LENGTH}
                                placeholder="Title"
                            />
                            <Textarea
                                {...register(`promptLibrary.${index}.prompt` as const, {
                                    maxLength: {
                                        value: PROMPT_DESCRIPTION_MAX_LENGTH,
                                        message: `Prompt cannot exceed ${PROMPT_DESCRIPTION_MAX_LENGTH} characters.`,
                                    },
                                })}
                                maxLength={PROMPT_DESCRIPTION_MAX_LENGTH}
                                placeholder="Prompt"
                            />
                            <Button
                                type="button"
                                className={styles.compactButton}
                                disabled={promptFields.length === 1}
                                onClick={() => removePrompt(index)}
                            >
                                Remove Prompt
                            </Button>
                        </div>
                    ))}
                    <div className={styles.addButtonRow}>
                        <Button
                            className={styles.compactButton}
                            type="button"
                            disabled={promptFields.length >= PROMPT_MAX_ITEMS}
                            onClick={() => appendPrompt({ title: '', prompt: '' })}
                        >
                            Add Prompt
                        </Button>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.fieldLabel}>
                        <Text className={styles.fieldName}>FAQs</Text>
                        <Tooltip
                            relationship="label"
                            positioning="after"
                            content={
                                <div className={styles.tooltipContent}>
                                    Add frequently asked questions and answers for this agent.
                                </div>
                            }
                        >
                            <Info24Regular className={styles.tooltipIcon} />
                        </Tooltip>
                    </div>
                    {faqFields.map((field, index) => (
                        <div key={field.id} className={styles.subItemCard}>
                            <Input
                                {...register(`faqs.${index}.question` as const, {
                                    maxLength: {
                                        value: SHORT_ENTRY_MAX_LENGTH,
                                        message: `Question cannot exceed ${SHORT_ENTRY_MAX_LENGTH} characters.`,
                                    },
                                })}
                                maxLength={SHORT_ENTRY_MAX_LENGTH}
                                placeholder="Question"
                            />
                            <Textarea
                                {...register(`faqs.${index}.answer` as const, {
                                    maxLength: {
                                        value: SHORT_ENTRY_MAX_LENGTH,
                                        message: `Answer cannot exceed ${SHORT_ENTRY_MAX_LENGTH} characters.`,
                                    },
                                })}
                                maxLength={SHORT_ENTRY_MAX_LENGTH}
                                placeholder="Answer"
                            />
                            <Button
                                type="button"
                                className={styles.compactButton}
                                disabled={faqFields.length === 1}
                                onClick={() => removeFaq(index)}
                            >
                                Remove FAQ
                            </Button>
                        </div>
                    ))}
                    <div className={styles.addButtonRow}>
                        <Button
                            className={styles.compactButton}
                            type="button"
                            disabled={faqFields.length >= FAQ_MAX_ITEMS}
                            onClick={() => appendFaq({ question: '', answer: '' })}
                        >
                            Add FAQ
                        </Button>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.fieldLabel}>
                        <Text className={styles.fieldName}>Agent Owner Info</Text>
                        <Tooltip
                            relationship="label"
                            positioning="after"
                            content={
                                <div className={styles.tooltipContent}>
                                    Add the email addresses for the owners of this agent.
                                </div>
                            }
                        >
                            <Info24Regular className={styles.tooltipIcon} />
                        </Tooltip>
                    </div>
                    {ownerFields.map((field, index) => (
                        <div key={field.id} className={styles.subItemCard}>
                            <Field
                                validationMessage={errors.ownerInfo?.[index]?.userPrincipalName?.message}
                                validationState={errors.ownerInfo?.[index]?.userPrincipalName ? 'error' : 'none'}
                            >
                                <Input
                                    {...register(`ownerInfo.${index}.userPrincipalName` as const, {
                                        validate: (value) => validateCompanyEmail(value),
                                    })}
                                    placeholder="owner@roberthalf.com"
                                />
                            </Field>
                            <Button
                                type="button"
                                className={styles.compactButton}
                                disabled={ownerFields.length === 1}
                                onClick={() => removeOwner(index)}
                            >
                                Remove Owner
                            </Button>
                        </div>
                    ))}
                    <div className={styles.addButtonRow}>
                        <Button
                            className={styles.compactButton}
                            type="button"
                            onClick={() =>
                                appendOwner({
                                    userId: '',
                                    givenName: '',
                                    surName: '',
                                    userPrincipalName: '',
                                    mail: '',
                                    photoUrl: '',
                                })
                            }
                        >
                            Add Owner
                        </Button>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.fieldLabel}>
                        <Text className={styles.fieldName}>Agent Support Address</Text>
                        <Tooltip
                            relationship="label"
                            positioning="after"
                            content={
                                <div className={styles.tooltipContent}>
                                    Provide a support contact address for this agent.
                                </div>
                            }
                        >
                            <Info24Regular className={styles.tooltipIcon} />
                        </Tooltip>
                    </div>
                    <div className={styles.subItemCard}>
                        <Field
                            validationMessage={errors.supportAddress?.message}
                            validationState={errors.supportAddress ? 'error' : 'none'}
                        >
                            <Input
                                {...register('supportAddress', {
                                    validate: (value) => validateCompanyEmail(value),
                                })}
                                placeholder="support@roberthalf.com"
                            />
                        </Field>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.fieldLabel}>
                        <Text className={styles.fieldName}>Agent Capabilities</Text>
                        <Tooltip
                            relationship="label"
                            positioning="after"
                            content={
                                <div className={styles.tooltipContent}>
                                    List the key capabilities this agent provides.
                                </div>
                            }
                        >
                            <Info24Regular className={styles.tooltipIcon} />
                        </Tooltip>
                    </div>
                    {capabilityFields.map((field, index) => (
                        <div key={field.id} className={styles.subItemCard}>
                            <div className={styles.inlineActionRow}>
                                <Input
                                    {...register(`capabilities.${index}.value` as const, {
                                        maxLength: {
                                            value: CAPABILITY_MAX_LENGTH,
                                            message: `Capability cannot exceed ${CAPABILITY_MAX_LENGTH} characters.`,
                                        },
                                    })}
                                    maxLength={CAPABILITY_MAX_LENGTH}
                                    placeholder="Capability"
                                />
                                <Button
                                    type="button"
                                    className={styles.compactButton}
                                    disabled={capabilityFields.length === 1}
                                    onClick={() => removeCapability(index)}
                                >
                                    Remove
                                </Button>
                            </div>
                        </div>
                    ))}
                    <div className={styles.addButtonRow}>
                        <Button
                            className={styles.compactButton}
                            type="button"
                            disabled={capabilityFields.length >= CAPABILITIES_MAX_ITEMS}
                            onClick={() => appendCapability({ value: '' })}
                        >
                            Add Capability
                        </Button>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.fieldLabel}>
                        <Text className={styles.fieldName}>Related Links</Text>
                        <Tooltip
                            relationship="label"
                            positioning="after"
                            content={
                                <div className={styles.tooltipContent}>Add related reference links for this agent.</div>
                            }
                        >
                            <Info24Regular className={styles.tooltipIcon} />
                        </Tooltip>
                    </div>
                    {relatedLinkFields.map((field, index) => (
                        <div key={field.id} className={styles.subItemCard}>
                            <Input
                                {...register(`relatedLinks.${index}.title` as const, {
                                    maxLength: {
                                        value: SHORT_ENTRY_MAX_LENGTH,
                                        message: `Link title cannot exceed ${SHORT_ENTRY_MAX_LENGTH} characters.`,
                                    },
                                })}
                                maxLength={SHORT_ENTRY_MAX_LENGTH}
                                placeholder="Link title"
                            />
                            <Input
                                {...register(`relatedLinks.${index}.link` as const, {
                                    maxLength: {
                                        value: SHORT_ENTRY_MAX_LENGTH,
                                        message: `Link cannot exceed ${SHORT_ENTRY_MAX_LENGTH} characters.`,
                                    },
                                })}
                                maxLength={SHORT_ENTRY_MAX_LENGTH}
                                placeholder="https://example.com"
                            />
                            <Button
                                type="button"
                                className={styles.compactButton}
                                disabled={relatedLinkFields.length === 1}
                                onClick={() => removeRelatedLink(index)}
                            >
                                Remove Link
                            </Button>
                        </div>
                    ))}
                    <div className={styles.addButtonRow}>
                        <Button
                            className={styles.compactButton}
                            type="button"
                            disabled={relatedLinkFields.length >= RELATED_LINK_MAX_ITEMS}
                            onClick={() => appendRelatedLink({ title: '', link: '' })}
                        >
                            Add Link
                        </Button>
                    </div>
                </div>

                <div className={styles.buttonRow}>
                    <Button appearance="secondary" onClick={vm.onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button appearance="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Creating...' : 'Create Agent'}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default CreateAgentPage;
